import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, X, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SNOOZE_KEY = "compass_checkin_snoozed_at";
const SNOOZE_MS = 12 * 60 * 60 * 1000; // 12 hours

// In-app reminder: nudges the user to check in every ~12 hours.
// (True server-sent push to all users requires a backend scheduler + web-push infra,
//  which this frontend-only app cannot provide — see notes.)
const CheckinReminder = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      if (snoozed && Date.now() - snoozed < SNOOZE_MS) return;
      try {
        const { data } = await api.get("/streaks");
        const last = data?.last_checkin && data.last_checkin !== "None" ? new Date(data.last_checkin).getTime() : 0;
        const due = !last || Date.now() - last >= SNOOZE_MS;
        if (due && !cancelled) {
          setShow(true);
          // Fire a browser notification if already permitted.
          if ("Notification" in window && Notification.permission === "granted") {
            try { new Notification("The Daily Compass", { body: "Time for your check-in 🧭", icon: "/compass-192.png" }); } catch (e) {}
          }
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const snooze = () => { localStorage.setItem(SNOOZE_KEY, String(Date.now())); setShow(false); };

  const checkin = async () => {
    try {
      await api.post("/streaks/checkin");
      toast.success("Checked in — your streak grows!");
      localStorage.setItem(SNOOZE_KEY, String(Date.now()));
      setShow(false);
    } catch (e) { toast.error("Could not check in"); }
  };

  const enableReminders = async () => {
    if (!("Notification" in window)) { toast.info("Notifications aren't supported in this browser"); return; }
    try {
      const p = await Notification.requestPermission();
      if (p === "granted") { toast.success("Reminders on — we'll nudge you to check in"); try { api.post("/notifications/register").catch(() => {}); } catch (e) {} }
    } catch (e) {}
  };

  if (!show) return null;

  return (
    <div data-testid="checkin-reminder" className="mx-4 sm:mx-6 mt-4 bg-gradient-to-br from-warning/15 to-warning/5 border border-warning/30 rounded-2xl p-4 flex items-center gap-4 animate-fade-up">
      <div className="w-10 h-10 rounded-full bg-warning/20 text-warning flex items-center justify-center shrink-0"><Flame className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Time for your check-in</p>
        <p className="text-sm text-muted-foreground">Keep your streak alive — it's been a while since your last one.</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button data-testid="reminder-enable" onClick={enableReminders} title="Enable reminders" className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground px-3 py-2 rounded-full hover:bg-secondary">
          <Bell className="w-4 h-4" />
        </button>
        <button data-testid="reminder-checkin" onClick={checkin}
          className="bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full hover:bg-atlantic active:scale-95 transition-all">
          Check in
        </button>
        <button data-testid="reminder-dismiss" onClick={snooze} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
      </div>
    </div>
  );
};

export default CheckinReminder;
